import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import PageSeo from '../seo/PageSeo';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL, pagePathToUrl } from '../seo/constants';
import PageLayout from '../components/PageLayout';

const sourceLinkSx = {
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { color: 'primary.dark' },
};

const PAGE_PATH = '/health-history';

const HealthHistory: React.FC = () => {
    const pageTitle = 'Standard Poodle Health & History | Austin, TX | Galia Standard Poodles';
    const pageDescription =
        'Standard poodle breed history, Addison\'s disease, bloat (GDV), and CHIC health testing — educational guide from Galia Standard Poodles in Austin, TX.';
    const pageUrl = pagePathToUrl(PAGE_PATH);

    return (
        <>
            <PageSeo
                title={pageTitle}
                description={pageDescription}
                path={PAGE_PATH}
                image={DEFAULT_OG_IMAGE}
                imageAlt="Calcifer, black AKC champion standard poodle in show stack pose"
                jsonLd={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name: pageTitle,
                        description: pageDescription,
                        url: pageUrl,
                        inLanguage: 'en-US',
                        isPartOf: {
                            '@type': 'WebSite',
                            name: SITE_NAME,
                            url: SITE_URL,
                        },
                        about: [
                            { '@type': 'Thing', name: 'Standard Poodle' },
                            { '@type': 'Thing', name: 'Addison\'s disease in dogs' },
                            { '@type': 'Thing', name: 'Gastric dilatation-volvulus' },
                        ],
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                name: 'Home',
                                item: `${SITE_URL}/`,
                            },
                            {
                                '@type': 'ListItem',
                                position: 2,
                                name: 'Health & History',
                                item: pageUrl,
                            },
                        ],
                    },
                ]}
            />
            <PageLayout>
                <Typography component="h1" variant="h4" gutterBottom>
                    Health & History
                </Typography>
                <Typography variant="body1" paragraph>
                    Standard Poodles are athletic, intelligent dogs with a long working history. Like all breeds,
                    they also carry health risks that thoughtful breeders and owners should understand. The
                    information below is meant to educate prospective families — it is not a substitute for
                    veterinary advice.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    A Brief History of the Poodle
                </Typography>
                <Typography variant="body1" paragraph>
                    Although the Poodle is the national dog of France and is often associated with French culture,
                    the breed likely developed as a water retriever in Germany. The name comes from the German word{' '}
                    <em>pudeln</em>, meaning to splash in water. In France, the breed is called <em>Caniche</em>,
                    from <em>canard</em> (duck), reflecting its history retrieving waterfowl for hunters.
                </Typography>
                <Typography variant="body1" paragraph>
                    According to the{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/dog-breeds/poodle-history/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        American Kennel Club
                    </Link>
                    , the Poodle is one of the oldest breeds. Curly-coated dogs with clipped hindquarters appear in
                    Roman tomb carvings and on Greek and Roman coins as early as 30 A.D. Over centuries, larger
                    Poodles worked as water retrievers while smaller dogs from the same litters served as companions.
                    Hunters trimmed the heavy coat on the legs, neck, and tail so dogs could swim more easily, while
                    leaving hair over the chest and joints to protect vital areas in cold water — the origin of the
                    breed&apos;s familiar trim.
                </Typography>
                <Typography variant="body1" paragraph>
                    The AKC recognized the Standard Poodle and Miniature Poodle as separate breeds in 1887. Both
                    belong to the Non-Sporting Group. The Toy variety was later recognized in the United States in
                    the early 20th century. Today, Standard Poodles remain versatile working dogs and companions,
                    excelling in obedience, agility, hunting, therapy work, and more — a long way from their origins
                    as duck dogs, but still very much an active, trainable breed.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Health Conditions to Know About
                </Typography>
                <Typography variant="body1" paragraph>
                    No breed is without health risk. At Galia Standard Poodles, we health-test our breeding dogs
                    through the{' '}
                    <Link href="https://www.ofa.org" target="_blank" rel="noopener noreferrer" sx={sourceLinkSx}>
                        Orthopedic Foundation for Animals (OFA)
                    </Link>
                    . Each of our breeding poodles earns a CHIC number, and we test beyond CHIC requirements. Some
                    serious conditions that affect Standard Poodles,
                    however, cannot yet be screened for with a simple genetic test. Two of the most important to
                    understand are Addison&apos;s disease and bloat.
                </Typography>

                <Typography component="h3" variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Addison&apos;s Disease (Hypoadrenocorticism)
                </Typography>
                <Typography variant="body1" paragraph>
                    Addison&apos;s disease occurs when the adrenal glands do not produce enough essential hormones,
                    particularly cortisol and aldosterone. These hormones help regulate stress response, blood
                    pressure, metabolism, and electrolyte balance. Without them, a dog&apos;s health can deteriorate
                    rapidly.
                </Typography>
                <Typography variant="body1" paragraph>
                    The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/health/addisons-disease/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    notes that any dog can develop Addison&apos;s disease, but Standard Poodles are among the breeds
                    predisposed to it, along with breeds such as Portuguese Water Dogs, Nova Scotia Duck Tolling
                    Retrievers, and Great Danes. It is most often seen in young to middle-aged dogs and may be more
                    common in females. The most common cause is immune-mediated destruction of the adrenal glands,
                    though other causes exist.
                </Typography>
                <Typography variant="body1" paragraph>
                    Signs can be vague and intermittent — lethargy, poor appetite, vomiting, or diarrhea — which makes
                    the disease easy to miss early on. Some dogs are first diagnosed during an <strong>Addisonian
                    crisis</strong>, when hormone levels drop critically low and the dog may go into shock or
                    collapse. This is a medical emergency.
                </Typography>
                <Typography variant="body1" paragraph>
                    Diagnosis is confirmed with an <strong>ACTH stimulation test</strong>, which measures how well the
                    adrenal glands respond to stimulation. There is currently <strong>no genetic screening test</strong>{' '}
                    that can tell a breeder or owner whether a dog will develop Addison&apos;s before symptoms appear,
                    though research continues in Standard Poodles and other affected breeds.
                </Typography>
                <Typography variant="body1" paragraph>
                    The good news: with prompt diagnosis and lifelong hormone replacement therapy — typically daily
                    oral steroids and monthly injectable mineralocorticoid medication — most dogs with Addison&apos;s
                    disease can live normal, full lives. The key is recognizing symptoms early and working closely with
                    a veterinarian.
                </Typography>

                <Typography component="h3" variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Bloat (Gastric Dilatation-Volvulus / GDV)
                </Typography>
                <Typography variant="body1" paragraph>
                    Bloat refers to gastric dilatation-volvulus, or <strong>GDV</strong> — a life-threatening
                    emergency in which the stomach fills with gas and may twist on itself, cutting off blood flow and
                    sending the dog into shock. Even with intensive treatment, bloat still kills a significant
                    percentage of affected dogs.
                </Typography>
                <Typography variant="body1" paragraph>
                    According to the{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/health/bloat-in-dogs/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>
                    , risk is strongly tied to chest conformation: deep, narrow-chested dogs are most affected.
                    Standard Poodles are among the large breeds at greatest risk, along with Great Danes, Saint
                    Bernards, Weimaraners, Irish Setters, Gordon Setters, and Doberman Pinschers. Males are about
                    twice as likely to bloat as females.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Warning signs</strong> include a distended or tight abdomen, unproductive retching (trying
                    to vomit with little or nothing coming up), restlessness, drooling, and signs of pain when the
                    belly is touched. If you suspect bloat, go to a veterinarian or emergency clinic immediately —
                    this is not a wait-and-see situation.
                </Typography>
                <Typography variant="body1" paragraph>
                    Emergency treatment involves stabilizing the dog and surgery to decompress and reposition the
                    stomach. Because up to 90% of affected dogs may bloat again, veterinarians often perform a{' '}
                    <strong>gastropexy</strong> — surgically tacking the stomach to the abdominal wall to help prevent
                    future twisting.                     Some owners of at-risk breeds discuss preventive (prophylactic) gastropexy with
                    their veterinarian, often at the time of spay or neuter. In the Austin area,{' '}
                    <Link
                        href="https://ctvsh.com/surgery/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        Central Texas Veterinary Specialty and Emergency Hospital
                    </Link>{' '}
                    performs gastropexy, including minimally invasive laparoscopic procedures.
                </Typography>
                <Typography variant="body1" paragraph>
                    While not every case can be prevented, the AKC highlights evidence-based steps that may reduce
                    risk:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body1">
                        Feed two or more smaller meals per day rather than one large meal.
                    </Typography>
                    <Typography component="li" variant="body1">
                        Slow fast eaters with a puzzle feeder or slow-feed bowl.
                    </Typography>
                    <Typography component="li" variant="body1">
                        Reduce stress and anxiety around mealtimes — for example, feed dogs separately if needed.
                    </Typography>
                    <Typography component="li" variant="body1">
                        Know your dog&apos;s family history; dogs with close relatives who have bloated are at higher
                        risk, and the AKC recommends those dogs not be used for breeding.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    What This Means for Our Program
                </Typography>
                <Typography variant="body1" paragraph>
                    We believe educated puppy families make the best long-term homes. We health-test our breeding dogs,
                    earn CHIC certification for each one, and go above and beyond CHIC requirements — breeding
                    thoughtfully with temperament and structure in mind. Because
                    Addison&apos;s disease and bloat cannot be eliminated through testing alone, we also encourage
                    every owner to know the signs of both conditions, maintain a relationship with a trusted
                    veterinarian, and reach out promptly if something seems wrong.
                </Typography>
                <Typography variant="body1" paragraph>
                    If you have questions about health in our lines or what to watch for in your puppy, we are always
                    happy to talk.
                </Typography>

                <Box
                    sx={{
                        bgcolor: '#f9f9f9',
                        p: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        mt: 3,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        <strong>Sources:</strong> Breed history and health information on this page is drawn primarily
                        from the{' '}
                        <Link
                            href="https://www.akc.org/expert-advice/dog-breeds/poodle-history/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC&apos;s Poodle history article
                        </Link>
                        ,{' '}
                        <Link
                            href="https://www.akc.org/expert-advice/health/addisons-disease/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC article on Addison&apos;s disease
                        </Link>
                        , and{' '}
                        <Link
                            href="https://www.akc.org/expert-advice/health/bloat-in-dogs/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC article on bloat in dogs
                        </Link>
                        . Additional context from the{' '}
                        <Link
                            href="https://www.akc.org/dog-breeds/poodle-standard/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC Standard Poodle breed page
                        </Link>
                        . This page is for educational purposes only and does not constitute veterinary advice.
                    </Typography>
                </Box>
            </PageLayout>
        </>
    );
};

export default HealthHistory;
